import { forwardRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-react'

interface PlanFeedbackProps {
    /** Called with the trimmed note; the host owns the planner round-trip. */
    onRevise: (feedback: string) => void
    isRevising?: boolean
}

/**
 * The one place a user tells the planner what to change about a drafted plan —
 * shared by creation review and GoalDetails → Redraft so both read the same.
 *
 * No visible label: the host's "Not quite right? Say what to change" invitation already asks,
 * and focuses this textarea through the forwarded ref (see PlanFeedbackInvite).
 *
 * The wrapper carries `data-plan-feedback` so a host with its own ⌘Enter binding
 * can tell "confirm the screen" from "revise the plan" (ReviewScreen does).
 */
const PlanFeedback = forwardRef<HTMLTextAreaElement, PlanFeedbackProps>(({ onRevise, isRevising }, ref) => {
    const [feedback, setFeedback] = useState('')
    const canRevise = feedback.trim().length > 0 && !isRevising

    const revise = () => {
        if (!canRevise) return
        onRevise(feedback.trim())
    }

    // The textarea drops its own outline so the ring wraps the whole composer instead: the global
    // 2px / 2px-offset keyboard ring, only while the textarea itself is :focus-visible.
    return (
        <section
            data-plan-feedback
            className="space-y-2 rounded-gt-md border border-border bg-muted/50 p-2.5 has-[textarea:focus-visible]:outline has-[textarea:focus-visible]:outline-2 has-[textarea:focus-visible]:outline-offset-2 has-[textarea:focus-visible]:outline-ring"
        >
            <textarea
                ref={ref}
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                onKeyDown={(e) => {
                    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                        e.preventDefault()
                        revise()
                    }
                }}
                placeholder="Fewer weekly sessions, make strength a milestone"
                aria-label="Changes to the plan"
                rows={2}
                className="w-full resize-none border-0 bg-transparent p-0 text-body-md touch-field-floor leading-relaxed text-foreground placeholder:text-muted-foreground/60 focus-visible:outline-none"
            />
            <div className="flex justify-end">
                <Button size="sm" variant="outline" onClick={revise} disabled={!canRevise} className="gap-1.5">
                    {isRevising && <Loader2 className="size-3.5 animate-spin" />}
                    {isRevising ? 'Revising…' : 'Revise plan'}
                    <kbd className="pointer-events-none inline-flex items-center gap-0.5 rounded bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                        <span>⌘</span>
                        <span>↵</span>
                    </kbd>
                </Button>
            </div>
        </section>
    )
})
PlanFeedback.displayName = 'PlanFeedback'

/** The top-of-screen invitation; scrolls to and focuses the PlanFeedback textarea. */
const PlanFeedbackInvite = ({ onClick }: { onClick: () => void }) => (
    <Button
        type="button"
        variant="link"
        onClick={onClick}
        className="h-auto min-h-touch p-0 text-body-sm text-foreground underline underline-offset-[3px] hover:text-foreground/80 md:min-h-0"
    >
        Not quite right? Say what to change
    </Button>
)

/** Bring the composer into view (it can sit below a scrolled body on short screens) and focus it. */
const focusPlanFeedback = (textarea: HTMLTextAreaElement | null) => {
    if (!textarea) return
    textarea.scrollIntoView({ block: 'nearest' })
    textarea.focus()
}

export default PlanFeedback
export { PlanFeedback, PlanFeedbackInvite, focusPlanFeedback }
